/**
 * PL。社内ドラフト2案を画面上部の「表示案」で切り替える(2026-09-10 指示)。
 *  ① シート版(横軸=会社): 現行「PL日報」の「1日」シートと同じ見た目。左に 大分類/中分類/項目、右に 会社列＋合計。
 *     期間は 日次／週次／月次(その期間の合計を1枚のシートに)。ブロック(全体／品川支店／在宅)もシートと同じ
 *  ② 階層版(横軸=期間): 科目の親子(アポ数＞目標/取得数 …)を縦に、日/週/月を横に(PlTreeView)
 *  - 取得件数 × 単価 = 売上見込 / 納品件数 × 単価 = 売上(実績)。**キャンセル分は引かない**(2026-09-24 D-140)
 *  - 取得件数は「日報/サマリ」で手修正した日はその値(会社×出勤形態×日)、無ければ日次集計(対応履歴)の合計(⑬指示)
 *  - 売上原価 = 出勤(品川の時給×実稼働)・交通費・在宅歩合(件数×単価)・在宅時給(時給×BB稼働)・タイミー
 *  - 経費 = 「日報/サマリ」の経費画面で入れた日ごとの金額(D-57)。①は現行シートと同じく構成比(実績アポ数の会社シェア)で会社へ配賦(D-48)、
 *    ②は按分せず「全社共通」の行にまとめる(P-22b)。Aj通話料 = 通話時間(分) × 2.88円は会社別に出る
 *  - 各行は行をクリックして展開し、アポインター別の根拠(取得数・報酬・通話料)まで追える(既定は閉じる。⑦指示)
 *  - 横スクロールはしない(⑦指示): 既定は稼働している会社だけを列に出す。納品件数の入力は「日報/サマリ」ページ
 *  - 目標・前提値は「月次設定」で入力(経費の月額入力は D-57 で廃止)
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import MonthSettingsModal from './MonthSettings';
import MultiSelect from '@core/components/MultiSelect';
import FilterBar from '@core/components/ui/FilterBar';
import MetricCard, { MetricRow } from '@core/components/ui/MetricCard';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge from '@core/components/ui/StatusBadge';
import TableDensity, { useTableDensity } from '@core/components/ui/TableDensity';
import { formatCount, formatPercent, formatPointDiff, formatScaledCurrency } from '@core/utils/formatters';
import { formatYen } from '@core/lib/money';
import {
  COST_STYLES,
  DELIVERY_STYLES,
  priceOn,
  type Company,
  type CostStyle,
  type DateStr,
  type Delivery,
  type DeliveryDetail,
  type DeliveryStyle,
  type MonthTargets,
} from '@core/lib/types';
import { apptOverrideMap, type StaffMetrics } from './calc';
import {  } from './dashboardRepo';
import { fetchExpenseDaily, fetchExpenseItems, type ExpenseDaily, type ExpenseItem } from './expenseRepo';
import PeriodToolbar from './PeriodToolbar';
import { buildCallActuals, actualCallFee, hasActuals, otherCallFee, type CallActuals } from './callActuals';
import { fetchAppliedCallCharges } from './monthCloseRepo';
import { fetchCallNumbers, type CallNumber } from '@core/masters/settingsPayRepo';
import PlSheetView from './PlSheetView';
import PlTreeView, { GRAN_LABELS, type Gran } from './PlTreeView';
import { collectKeys, MONEY_UNIT_LABELS, toneOf, type Fmt, type MoneyUnit, type PlRow } from './plRow';
import { usePeriodData } from './usePeriod';

/** ①シート版(会社×科目) / ②階層版(期間×階層)。社内ドラフト2案 */
type PlView = 'sheet' | 'tree';
const VIEW_LABELS: Record<PlView, string> = { sheet: '① シート版(横軸=会社)', tree: '② 階層版(横軸=期間)' };
type ColumnOrder = 'master' | 'sales' | 'appts';
/** 1日シートの3ブロック(全体／品川支店／在宅)。②は全体のみ */
type Scope = 'all' | 'shinagawa' | 'zaitaku';
const SCOPE_LABELS: Record<Scope, string> = { all: '全体', shinagawa: '出勤', zaitaku: '在宅' };

/** 支店で絞った納品件数(1日シートの品川ブロック・在宅ブロックに相当)。counts も絞った内訳で作り直す */
function scopeDeliveries(deliveries: Delivery[], scope: Scope): Delivery[] {
  if (scope === 'all') return deliveries;
  const dStyles: DeliveryStyle[] = scope === 'shinagawa' ? ['shinagawa'] : ['buai', 'jikyu'];
  const cStyles: CostStyle[] = scope === 'shinagawa' ? ['shinagawa', 'timee'] : ['buai', 'jikyu'];
  const pickCost = (src: Partial<Record<CostStyle, number>> | undefined) => {
    if (!src) return undefined;
    const out: Partial<Record<CostStyle, number>> = {};
    for (const k of COST_STYLES) if (cStyles.includes(k) && src[k] !== undefined) out[k] = src[k];
    return Object.keys(out).length > 0 ? out : undefined;
  };
  return deliveries.map((d) => {
    const details: Record<string, DeliveryDetail> = {};
    const counts: Record<string, number> = {};
    for (const [cid, det] of Object.entries(d.details ?? {})) {
      const by_style: DeliveryDetail['by_style'] = {};
      for (const k of DELIVERY_STYLES) if (dStyles.includes(k) && det.by_style[k] !== undefined) by_style[k] = det.by_style[k];
      let appointments: DeliveryDetail['appointments'];
      if (det.appointments) {
        const a: NonNullable<DeliveryDetail['appointments']> = {};
        for (const k of DELIVERY_STYLES) if (dStyles.includes(k) && det.appointments[k] !== undefined) a[k] = det.appointments[k];
        if (Object.keys(a).length > 0) appointments = a;
      }
      details[cid] = { ...det, by_style, appointments, labor: pickCost(det.labor), commute: pickCost(det.commute) };
      counts[cid] = Object.values(by_style).reduce((s, x) => s + (x ?? 0), 0);
    }
    return { ...d, counts, details };
  });
}

export default function PlPage() {
  const period = usePeriodData();
  const { rows, targets, companies, deliveries, loading, range, reload } = period;
  const { isAdmin } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** 社内ドラフト2案の切替(①=1日シートと同じ見た目・横軸は会社 / ②=科目の階層・横軸は期間) */
  const [view, setView] = useState<PlView>('sheet');
  /** ①の支店ブロック(1日シートの 全体／品川支店／在宅) */
  const [scope, setScope] = useState<Scope>('all');
  /** ①の行の開閉(アポインター別の内訳) */
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  /** ①の列(会社)の絞り込み・並び順・0の行 */
  const [companySel, setCompanySel] = useState<Set<string>>(new Set());
  const [order, setOrder] = useState<ColumnOrder>('master');
  const [hideZero, setHideZero] = useState<'hide' | 'show'>('hide');
  /** 横軸の単位(②のみ)。日=その月の1日〜末日 / 週=月〜日 / 月=その年の1月〜12月 */
  const [gran, setGran] = useState<Gran>('day');
  const setGranAndRange = (g: Gran) => {
    setGran(g);
    period.setMode(g === 'day' ? 'month' : g === 'week' ? 'week' : 'year');
  };
  /** ①は 日次/週次/月次(その期間の合計を1枚のシートに)、②は 横軸の単位に合わせた範囲 */
  const setViewAndRange = (v: PlView) => {
    setView(v);
    if (v === 'sheet') period.setMode(period.mode === 'year' ? 'month' : period.mode);
    else period.setMode(gran === 'day' ? 'month' : gran === 'week' ? 'week' : 'year');
  };
  /** 金額の単位(2026-09-10 指示: 万/千に切り替えられるように)。上段カードと PL 本体の両方に効く */
  const [unit, setUnit] = useState<MoneyUnit>('yen');
  /** 行の高さ(標準 44px / 詰める 36px) */
  const [density, setDensity] = useTableDensity('cc.pl.density');
  // 開いた直後: ①は日次/週次/月次のまま(year だけ月次に寄せる)
  useEffect(() => {
    if (period.mode === 'year') period.setMode('month');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** 日ごとの経費(P-22: PL の経費はこの入力値だけを使う)と、その項目 */
  const [expenses, setExpenses] = useState<ExpenseDaily>(new Map());
  const [expenseItems, setExpenseItems] = useState<ExpenseItem[]>([]);
  useEffect(() => {
    let alive = true;
    void Promise.all([fetchExpenseDaily(range.from, range.to), fetchExpenseItems()])
      .then(([m, its]) => {
        if (!alive) return;
        setExpenses(m);
        setExpenseItems(its);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [range.from, range.to]);
  const expenseTotal = useMemo(() => {
    let s = 0;
    for (const v of expenses.values()) for (const x of Object.values(v)) s += x;
    return s;
  }, [expenses]);

  const activeCompanies = useMemo(() => companies.filter((c: Company) => c.status === 'active').sort((a, b) => a.id.localeCompare(b.id)), [companies]);
  // ①の支店ブロック: 日次集計・納品件数をその支店で絞る。経費・管理者報酬は支店に配賦しない(シートの支店ブロックも0)
  const scopedRows = useMemo(
    () => (scope === 'all' ? rows : rows.filter((r: StaffMetrics) => (scope === 'shinagawa' ? r.work_style === 'shinagawa' || r.work_style === 'timee' || r.is_manager : r.work_style === 'zaitaku'))),
    [rows, scope],
  );
  const scopedDeliveries = useMemo(() => scopeDeliveries(deliveries, scope), [deliveries, scope]);
  const scopedTargets = useMemo(() => (scope === 'all' || !targets ? targets : { ...targets, manager_bonus_rates: [], company_targets: {} }), [targets, scope]);
  const scopedExpenses = useMemo(() => (scope === 'all' ? expenses : (new Map() as ExpenseDaily)), [expenses, scope]);
  /** 列に出す会社: 既定は期間内に取得か納品がある会社だけ(横スクロールを出さないため)。
   *  ブロック(全体/品川支店/在宅)で列が変わらないよう、絞り込みは支店で絞る前の全体で判定する(2026-09-11 指示) */
  /** 通話料の実績(「PLに反映する」を押した月だけ)。押していない月は推定のまま */
  const [callActuals, setCallActuals] = useState<CallActuals | null>(null);
  useEffect(() => {
    let alive = true;
    const months = [...new Set(range.dates.map((d) => d.slice(0, 7)))];
    void Promise.all([Promise.all(months.map(async (m) => ({ month: m, calls: await fetchAppliedCallCharges(m) }))), fetchCallNumbers()])
      .then(([list, nums]) => {
        if (!alive) return;
        const applied = list.filter((x) => x.calls).map((x) => ({ month: x.month, calls: x.calls! }));
        setCallActuals(buildCallActuals(applied, nums.items as CallNumber[]));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [range.dates]);

  const cols = useMemo(() => {
    const appts = new Map<string, number>();
    for (const r of rows) if (r.company_id) appts.set(r.company_id, (appts.get(r.company_id) ?? 0) + r.appointments);
    const del = new Map<string, number>();
    for (const d of deliveries) for (const [k, v] of Object.entries(d.counts ?? {})) del.set(k, (del.get(k) ?? 0) + v);
    let list = activeCompanies;
    if (companySel.size === 0) list = list.filter((c) => (appts.get(c.id) ?? 0) > 0 || (del.get(c.id) ?? 0) > 0);
    else list = list.filter((c) => companySel.has(c.id));
    if (order === 'appts') list = [...list].sort((a, b) => (appts.get(b.id) ?? 0) - (appts.get(a.id) ?? 0));
    if (order === 'sales') list = [...list].sort((a, b) => (del.get(b.id) ?? 0) * priceOn(b, range.to) - (del.get(a.id) ?? 0) * priceOn(a, range.to));
    return list;
  }, [activeCompanies, rows, deliveries, companySel, order, range.to]);

  const pl = useMemo(
    () => (scopedTargets ? buildPl(scopedRows, cols, scopedDeliveries, scopedTargets, range.to, range.dates.length, scopedExpenses, expenseItems, callActuals, range.dates) : null),
    [scopedRows, cols, scopedDeliveries, scopedTargets, range.to, range.dates.length, scopedExpenses, expenseItems],
  );
  /** ブロックを切り替えても行が増減しないよう、隠す行は「全体」で判定する(2026-09-11 指示) */
  const plAll = useMemo(
    () => (targets && scope !== 'all' ? buildPl(rows, cols, deliveries, targets, range.to, range.dates.length, expenses, expenseItems, callActuals, range.dates) : null),
    [targets, scope, rows, cols, deliveries, range.to, range.dates.length, expenses, expenseItems],
  );

  /** ①で 0 だけの行を隠す(シートも空欄の科目は見ない)。判定は「全体」の値で行う */
  const visibleRows = useMemo(() => {
    if (!pl) return [];
    if (hideZero === 'show') return pl.rows;
    const isZero = (r: PlRow) => !r.section && !r.bold && (r.total === null || r.total === 0) && Object.values(r.values).every((v) => v === null || v === 0);
    const hidden = new Set((plAll ?? pl).rows.filter(isZero).map((r) => r.key));
    return pl.rows.filter((r) => !hidden.has(r.key));
  }, [pl, plAll, hideZero]);
  const allKeys = useMemo(() => (pl ? collectKeys(pl.rows) : []), [pl]);

  const scaleValue = (v: number) => formatScaledCurrency(v, unit);
  const scaleUnit = unit === 'yen' ? undefined : MONEY_UNIT_LABELS[unit];
  const costOver = pl && pl.costRate !== null && targets ? pl.costRate - targets.cost_rate_total : null;

  return (
    <div className="screen full">
      <PageHeader
        title="PL"
        meta={range.label}
        actions={
          isAdmin && (
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setSettingsOpen(true)}>
              月次設定(目標・前提値)
            </button>
          )
        }
      />
      <PeriodToolbar
        period={period}
        showModes={view === 'sheet'}
        right={
          <>
            <SegmentedControl label="表示案" showLabel options={(Object.keys(VIEW_LABELS) as PlView[]).map((v) => ({ key: v, label: VIEW_LABELS[v] }))} value={view} onChange={setViewAndRange} />
            {view === 'tree' && <SegmentedControl label="横軸" showLabel options={(Object.keys(GRAN_LABELS) as Gran[]).map((g) => ({ key: g, label: GRAN_LABELS[g] }))} value={gran} onChange={setGranAndRange} />}
            <SegmentedControl label="単位" showLabel options={(Object.keys(MONEY_UNIT_LABELS) as MoneyUnit[]).map((u) => ({ key: u, label: MONEY_UNIT_LABELS[u] }))} value={unit} onChange={setUnit} />
          </>
        }
      />
      {loading || !targets || !pl ? (
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      ) : (
        <>
          {/* ---- 上段KPI ---- */}
          <div className="kpi-grid">
            <MetricCard label="売上小計" value={scaleValue(pl.salesSubtotal)} unit={scaleUnit} footnote="納品 × 単価">
              <MetricRow label="納品件数" value={formatCount(pl.visits, { unit: '件' })} />
              <MetricRow label="実績アポ数" value={formatCount(pl.appointments, { unit: '件' })} />
              <MetricRow label="売上見込(取得 × 単価)" value={`${scaleValue(pl.expectedSales)}${scaleUnit ?? ''}`} />
            </MetricCard>
            <MetricCard label="売上原価(アポ報酬)" value={scaleValue(pl.costSubtotal)} unit={scaleUnit} footnote="出勤・交通費・在宅歩合・在宅時給・タイミー" />
            <MetricCard
              label="粗利"
              value={scaleValue(pl.gross)}
              unit={scaleUnit}
              valueTone={pl.gross < 0 ? 'danger' : undefined}
              status={pl.costRate === null ? <StatusBadge kind="noData" label="判定不可" /> : costOver !== null && costOver > 0 ? <StatusBadge kind="danger" label="原価率 超過" /> : <StatusBadge kind="info" label="原価率 目標内" />}
              target={`原価率 ${formatPercent(pl.costRate)}(目標 ${formatPercent(targets.cost_rate_total)})`}
              diff={costOver !== null ? `目標比 ${formatPointDiff(costOver)}` : undefined}
              diffTone={costOver !== null && costOver > 0 ? 'danger' : 'neutral'}
            >
              <MetricRow label="粗利率" value={formatPercent(pl.grossRate)} />
            </MetricCard>
            <MetricCard label="経費(日報/サマリの入力)" value={scaleValue(expenseTotal)} unit={scaleUnit} footnote="部門利益は yojitsu 側で確定">
              <MetricRow label="粗利 − 経費" value={`${scaleValue(pl.gross - expenseTotal)}${scaleUnit ?? ''}`} tone={pl.gross - expenseTotal < 0 ? 'danger' : undefined} />
            </MetricCard>
          </div>

          {/* ---- PL本体(社内ドラフト2案: ①1日シートと同じ並び ／ ②科目の階層) ---- */}
          <Panel
            title={`PL(${range.label})`}
            meta={<StatusBadge kind="info" label={view === 'sheet' ? '① シート版' : '② 階層版'} />}
            actions={
              <>
                <TableDensity value={density} onChange={setDensity} />
                {view === 'sheet' && (
                  <>
                    <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setOpen(new Set(allKeys))}>
                      すべて開く
                    </button>
                    <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setOpen(new Set())}>
                      すべて閉じる
                    </button>
                  </>
                )}
              </>
            }
            toolbar={
              view === 'sheet' ? (
                <FilterBar label="PLの表示">
                  <SegmentedControl label="ブロック" showLabel options={(Object.keys(SCOPE_LABELS) as Scope[]).map((s) => ({ key: s, label: SCOPE_LABELS[s] }))} value={scope} onChange={setScope} />
                  <MultiSelect label="会社" options={activeCompanies.map((c) => ({ key: c.id, label: c.name }))} value={companySel} onChange={setCompanySel} allLabel="稼働している会社" />
                  <SegmentedControl
                    label="並び順"
                    showLabel
                    options={[
                      { key: 'master', label: '会社マスタ順' },
                      { key: 'sales', label: '売上順' },
                      { key: 'appts', label: '実績アポ数順' },
                    ]}
                    value={order}
                    onChange={setOrder}
                  />
                  <SegmentedControl
                    label="0の行"
                    showLabel
                    options={[
                      { key: 'hide', label: '隠す' },
                      { key: 'show', label: '出す' },
                    ]}
                    value={hideZero}
                    onChange={setHideZero}
                  />
                </FilterBar>
              ) : undefined
            }
            note={
              view === 'sheet' ? (
                <>
                  現行「1日」シートの並び(構成比 → アポ数 → 売上 → 売上原価 → 粗利 → 経費 → 営業利益 → 営業外 → 経常利益)。行を押すとアポインター別。経費は「日報/サマリ」で入れた日ごとの金額を構成比で会社へ配賦。
                  管理者報酬は経費側(売上原価に含めない)。納品件数は「日報/サマリ」から。
                  {scope !== 'all' && `「${SCOPE_LABELS[scope]}」ブロックは日次集計・納品件数をその支店で絞った値。経費と管理者報酬は支店へ配賦しない(シートの支店ブロックも0)ため、経費はその支店の通話料だけになる。`}
                </>
              ) : (
                '経費は「日報/サマリ」の経費画面で入れた日ごとの金額(PLには直接入力しません)。Aj通話料は通話時間からの自動計算。管理者報酬は営業外経費。納品件数・人件費・交通費も「日報/サマリ」から。'
              )
            }
          >
            {view === 'sheet' ? (
              <PlSheetView rows={visibleRows} cols={cols} open={open} toggle={toggle} costTarget={targets.cost_rate_total ?? 44.5} unit={unit} compact={density === 'compact'} />
            ) : (
              <PlTreeView rows={rows} deliveries={deliveries} companies={activeCompanies} targets={targets} dates={range.dates} gran={gran} expenses={expenses} items={expenseItems} unit={unit} compact={density === 'compact'} callActuals={callActuals} />
            )}
          </Panel>
        </>
      )}

      {settingsOpen && targets && (
        <MonthSettingsModal
          month={range.anchor.slice(0, 7)}
          targets={targets}
          companies={activeCompanies}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => {
            setSettingsOpen(false);
            void reload();
          }}
        />
      )}
    </div>
  );
}

// ---- 集計(1日シートの計算式) ----------------------------------------------------


/** トップ画面(HomePage)でも同じ数字を出すため export している。計算式は現行「1日」シート */
export function buildPl(
  rows: StaffMetrics[],
  cols: Company[],
  deliveries: Delivery[],
  targets: MonthTargets,
  priceDate: string,
  days: number,
  /** 日ごとの経費(D-57。PL の経費はこれだけを使う)とその項目 */
  expenses: ExpenseDaily,
  items: ExpenseItem[],
  /** 通話料の実績(「PLに反映する」を押した月だけ)。無ければ推定のまま(2026-09-28) */
  callActuals?: CallActuals | null,
  /** 期間の日付(実績を日ごとに足すのに使う) */
  plDates: DateStr[] = [],
) {
  const ids = cols.map((c) => c.id);
  const zero = (): Record<string, number | null> => Object.fromEntries(ids.map((id) => [id, 0]));
  const sumRow = (pred: (r: StaffMetrics) => boolean, pick: (r: StaffMetrics) => number): Record<string, number | null> => {
    const v = zero();
    for (const r of rows) {
      if (!r.company_id || !(r.company_id in v) || !pred(r)) continue;
      v[r.company_id] = (v[r.company_id] ?? 0) + pick(r);
    }
    return v;
  };
  const totalOf = (v: Record<string, number | null>) => ids.reduce((s, id) => s + (v[id] ?? 0), 0);
  const map = (v: Record<string, number | null>, f: (x: number, id: string) => number | null) => Object.fromEntries(ids.map((id) => [id, f(v[id] ?? 0, id)])) as Record<string, number | null>;

  /** アポインター別の内訳行(親行の展開用) */
  const byAppointer = (pred: (r: StaffMetrics) => boolean, pick: (r: StaffMetrics) => number, fmt: Fmt, keyPrefix: string): PlRow[] => {
    const names = new Map<string, string>();
    for (const r of rows) if (pred(r)) names.set(r.op_id, r.name);
    return [...names.entries()]
      .map(([opId, name]) => {
        const v = sumRow((r) => pred(r) && r.op_id === opId, pick);
        return { key: `${keyPrefix}_${opId}`, label: name, fmt, values: v, total: totalOf(v), level: 1 } as PlRow;
      })
      // 金額が 0 のアポインターも出す(OPレポート未取込の日は稼働時間が無く 0 になるため。2026-09-11 指示)
      .sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
  };

  // ---- アポ数 ----
  const isShinagawa = (r: StaffMetrics) => !r.is_manager && (r.work_style === 'shinagawa' || r.work_style === 'timee');
  const isBuai = (r: StaffMetrics) => !r.is_manager && r.category === 'buai';
  const isJikyu = (r: StaffMetrics) => !r.is_manager && (r.category === 'zaitaku_jikyu' || r.category === 'white');
  const isManager = (r: StaffMetrics) => r.is_manager;
  const isTimee = (r: StaffMetrics) => r.work_style === 'timee';

  // 取得件数は「納品件数の入力」で手修正した日はその値(会社×出勤形態×日。⑬指示)、無ければ日次集計の合計
  const overrides = apptOverrideMap(deliveries);
  const apOf = (pred: (r: StaffMetrics) => boolean, style: DeliveryStyle): Record<string, number | null> => {
    const v = zero();
    const seen = new Set<string>();
    for (const r of rows) {
      if (!r.company_id || !(r.company_id in v) || !pred(r)) continue;
      const key = `${r.date}|${r.company_id}|${style}`;
      const o = overrides.get(key);
      if (o !== undefined) {
        if (!seen.has(key)) {
          seen.add(key);
          v[r.company_id] = (v[r.company_id] ?? 0) + o;
        }
        continue;
      }
      v[r.company_id] = (v[r.company_id] ?? 0) + r.appointments;
    }
    // 日次集計が無い日でも手修正があれば反映
    for (const [key, o] of overrides) {
      const [date, cid, st] = key.split('|');
      if (st !== style || !cid || !(cid in v) || seen.has(key)) continue;
      if (!rows.some((r) => r.date === date && r.company_id === cid && pred(r))) v[cid] = (v[cid] ?? 0) + o;
    }
    return v;
  };
  const apShinagawa = apOf((r) => isShinagawa(r) || isManager(r), 'shinagawa');
  const apBuai = apOf(isBuai, 'buai');
  const apJikyu = apOf(isJikyu, 'jikyu');
  const apActual = map(apShinagawa, (x, id) => x + (apBuai[id] ?? 0) + (apJikyu[id] ?? 0));
  const appointments = totalOf(apActual);
  const share = map(apActual, (x) => (appointments > 0 ? (x / appointments) * 100 : 0));

  // 目標(件/日 × 日数)
  const target = map(zero(), (_x, id) => (targets.company_targets[id] ?? 0) * days);
  const targetTotal = totalOf(target);
  const targetDiff = map(apActual, (x, id) => x - (target[id] ?? 0));
  const targetRatio = map(apActual, (x, id) => ((target[id] ?? 0) > 0 ? (x / (target[id] ?? 1)) * 100 : null));

  // 納品件数
  const visits = zero();
  for (const d of deliveries) for (const [id, n] of Object.entries(d.counts ?? {})) if (id in visits) visits[id] = (visits[id] ?? 0) + n;
  const visitsTotal = totalOf(visits);

  // ---- 売上 ----
  const unit = Object.fromEntries(cols.map((c) => [c.id, priceOn(c, priceDate)])) as Record<string, number>;
  const sales = map(visits, (x, id) => x * unit[id]!);
  // キャンセル分(売上 × キャンセル率)は引かない(2026-09-24 発注者指示「CXLはPLのキャンセルは無しに」。D-140)
  const salesSub = sales;
  const expected = map(apActual, (x, id) => x * unit[id]!);

  // ---- 売上原価(アポ報酬) ----
  // 人件費・交通費は「納品件数の入力」で 日×会社×出勤形態×報酬区分 に入力があればその値、無ければ日次集計の自動計算
  const costKey = (r: StaffMetrics): CostStyle => (r.work_style === 'timee' ? 'timee' : r.work_style === 'zaitaku' ? (r.pay_type === 'commission' ? 'buai' : 'jikyu') : 'shinagawa');
  const laborAuto = new Map<string, number>();
  const commuteAuto = new Map<string, number>();
  for (const r of rows) {
    if (!r.company_id || r.is_manager) continue;
    const k = `${r.date}|${r.company_id}|${costKey(r)}`;
    laborAuto.set(k, (laborAuto.get(k) ?? 0) + r.pay - r.commute);
    commuteAuto.set(k, (commuteAuto.get(k) ?? 0) + r.commute);
  }
  const inputsOf = (field: 'labor' | 'commute') => {
    const m = new Map<string, number>();
    for (const d of deliveries) for (const [cid, det] of Object.entries(d.details ?? {})) for (const [k, v] of Object.entries(det[field] ?? {})) if (v !== undefined) m.set(`${d.date}|${cid}|${k}`, v);
    return m;
  };
  const laborIn = inputsOf('labor');
  const commuteIn = inputsOf('commute');
  const costOf = (auto: Map<string, number>, input: Map<string, number>, ...styles: CostStyle[]): Record<string, number | null> => {
    const v = zero();
    for (const key of new Set([...auto.keys(), ...input.keys()])) {
      const [, cid, st] = key.split('|');
      if (!cid || !(cid in v) || !styles.includes(st as CostStyle)) continue;
      v[cid] = (v[cid] ?? 0) + (input.get(key) ?? auto.get(key) ?? 0);
    }
    return v;
  };
  const inputNote = (m: Map<string, number>) => (m.size > 0 ? '。「納品件数の入力」で入力があった日はその値を採用' : '');
  const costShinagawa = costOf(laborAuto, laborIn, 'shinagawa');
  const costCommute = costOf(commuteAuto, commuteIn, 'shinagawa', 'timee');
  const costBuai = costOf(laborAuto, laborIn, 'buai');
  const costJikyu = costOf(laborAuto, laborIn, 'jikyu');
  const costTimee = costOf(laborAuto, laborIn, 'timee');
  // タイミー振込手数料 = その日に稼働したタイミーの人数 × 単価(現行 220円。D-52)。人数は 日×会社×人 で数える
  const timeeHeads = zero();
  const seenTimee = new Set<string>();
  for (const r of rows) {
    if (!r.company_id || !(r.company_id in timeeHeads) || !isTimee(r) || (r.worked_hours <= 0 && r.calls <= 0)) continue;
    const key = `${r.date}|${r.company_id}|${r.op_id}`;
    if (seenTimee.has(key)) continue;
    seenTimee.add(key);
    timeeHeads[r.company_id] = (timeeHeads[r.company_id] ?? 0) + 1;
  }
  const costTimeeFee = map(timeeHeads, (n) => n * targets.timee_fee_per_person);
  const costSub = map(costShinagawa, (x, id) => x + (costCommute[id] ?? 0) + (costBuai[id] ?? 0) + (costJikyu[id] ?? 0) + (costTimee[id] ?? 0) + (costTimeeFee[id] ?? 0));
  const gross = map(salesSub, (x, id) => x - (costSub[id] ?? 0));
  const grossRate = map(gross, (x, id) => ((salesSub[id] ?? 0) > 0 ? (x / (salesSub[id] ?? 1)) * 100 : null));
  const costRate = map(costSub, (x, id) => ((salesSub[id] ?? 0) > 0 ? (x / (salesSub[id] ?? 1)) * 100 : null));

  // ---- 経費(D-57: 「日報/サマリ」の経費画面で入れた日ごとの金額。会社へは現行シートと同じく構成比で配賦。D-48) ----
  const expenseRows: PlRow[] = items.map((it) => {
    let amount = 0;
    for (const [, v] of expenses) amount += v[it.id] ?? 0;
    const v = map(share, (s) => Math.round((amount * s) / 100));
    return {
      key: `exp_${it.id}`,
      label: it.name,
      fmt: 'yen' as Fmt,
      values: v,
      total: amount,
      hint: `「日報/サマリ」の経費画面で入れた ${days}日分の合計 ¥${formatYen(amount)} を構成比(実績アポ数の会社シェア)で配賦`,
      better: '低いほど良い',
    } as PlRow;
  });
  const callEstimate = sumRow(() => true, (r) => (r.talk_sec / 60) * targets.call_rate_per_min);
  /** 実績を使う日があるか(使う月は実績、それ以外の日は推定) */
  const useActual = callActuals ? hasActuals(callActuals, plDates) : false;
  const callActual = (() => {
    const v = zero();
    if (!callActuals) return v;
    for (const id of ids) {
      let sum = 0;
      for (const d of plDates) {
        const a = actualCallFee(callActuals, id, d);
        sum += a === null ? 0 : a;
      }
      v[id] = sum;
    }
    return v;
  })();
  /** 実績を使う日は実績、使わない日は推定 */
  const callFee = useActual ? callActual : callEstimate;
  const otherFee = useActual && callActuals ? plDates.reduce((s, d) => s + (otherCallFee(callActuals, d) ?? 0), 0) : 0;
  const callFeeRow: PlRow = {
    key: 'exp_call',
    label: useActual ? 'Aj通話料(実績)' : 'Aj通話料',
    fmt: 'yen',
    values: map(callFee, (x) => Math.round(x)),
    total: Math.round(totalOf(callFee)) + (useActual ? otherFee : 0),
    hint: useActual
      ? '月締め作業③で取り込み、「PLに反映する」を押した実額(アジャストワンの請求明細)。会社に紐づく番号は会社別、それ以外は「その他(CC事業部以外)」'
      : `通話時間(分) × ${targets.call_rate_per_min}円(現行の1日シート合計行の通話料)`,
    better: '低いほど良い',
    children: useActual
      ? [{ key: 'exp_call_other', label: 'その他(CC事業部以外)', fmt: 'yen' as Fmt, values: zero(), total: otherFee, level: 1, hint: '会社に紐づかない発信番号(社用番号・グループ会社など)の通話料。会社の列には入りません' } as PlRow]
      : byAppointer(() => true, (r) => (r.talk_sec / 60) * targets.call_rate_per_min, 'yen', 'call'),
  };
  const expenseTotal = expenseRows.reduce((s, r) => s + (r.total ?? 0), 0) + (callFeeRow.total ?? 0);

  const grossTotal = totalOf(gross);
  const salesSubTotal = totalOf(salesSub);
  const plRows: PlRow[] = [
    { key: 'share', label: '構成比', fmt: 'pct', values: share, total: 100, summary: true, hint: '実績アポ数 ÷ 合計。経費の配賦に使う' },
    { key: 'sec_target', label: 'アポ数 ／ 目標', fmt: 'int', values: {}, total: null, section: true, hint: '会社別の目標(件/日)は「月次設定」で入力' },
    { key: 'target', label: 'アポ数(目標)', fmt: 'int', values: target, total: targetTotal, hint: '月次設定の会社別目標(件/日) × 期間日数' },
    { key: 'target_diff', label: '目標差異', fmt: 'int', values: targetDiff, total: appointments - targetTotal, hint: '実績アポ数 − 目標', better: 'プラスが良い' },
    { key: 'target_ratio', label: '目標比', fmt: 'ratio', values: targetRatio, total: targetTotal > 0 ? (appointments / targetTotal) * 100 : null, summary: true, hint: '実績アポ数 ÷ 目標', better: '100%以上が良い' },
    { key: 'sec_ap', label: 'アポ数 ／ 取得数', fmt: 'int', values: {}, total: null, section: true, hint: '行をクリックでアポインター別' },
    {
      key: 'ap_shinagawa',
      label: '予約アポ数',
      fmt: 'int',
      values: apShinagawa,
      total: totalOf(apShinagawa),
      hint: '出勤・タイミー・管理者の取得数',
      better: '多いほど良い',
      children: byAppointer((r) => isShinagawa(r) || isManager(r), (r) => r.appointments, 'int', 'aps'),
    },
    { key: 'ap_buai', label: '在宅歩合アポ数', fmt: 'int', values: apBuai, total: totalOf(apBuai), hint: '在宅歩合の取得数', better: '多いほど良い', children: byAppointer(isBuai, (r) => r.appointments, 'int', 'apb') },
    { key: 'ap_jikyu', label: '在宅時給アポ数', fmt: 'int', values: apJikyu, total: totalOf(apJikyu), hint: '在宅時給と不用品の取得数', better: '多いほど良い', children: byAppointer(isJikyu, (r) => r.appointments, 'int', 'apj') },
    { key: 'ap_actual', label: '実績アポ数', fmt: 'int', values: apActual, total: appointments, bold: true },
    /* 「訪問数」の行は 2026-09-21 発注者指示で削除(訪問数の別入力は保留。D-115)。売上は 納品件数 × 単価 のまま */
    { key: 'sec_sales', label: '売上', fmt: 'yen', values: {}, total: null, section: true },
    { key: 'unit', label: 'アポ単価', fmt: 'price', values: unit, total: null, hint: '会社マスタの単価(改訂履歴からその日の単価)' },
    { key: 'sales', label: '売上', fmt: 'yen', values: sales, total: totalOf(sales), hint: '納品件数 × アポ単価', better: '高いほど良い' },
    { key: 'sales_sub', label: '小計', fmt: 'yen', values: salesSub, total: salesSubTotal, bold: true, summary: true },
    { key: 'sec_cost', label: '売上原価 ／ アポ報酬', fmt: 'yen', values: {}, total: null, section: true, hint: '行をクリックでアポインター別' },
    {
      key: 'cost_shinagawa',
      label: '出勤',
      fmt: 'yen',
      values: costShinagawa,
      total: totalOf(costShinagawa),
      hint: `出勤の時給 × 実稼働時間${inputNote(laborIn)}(アポインター別は自動計算の内訳)`,
      better: '売上に対して低いほど良い',
      children: byAppointer((r) => isShinagawa(r) && !isTimee(r), (r) => r.pay - r.commute, 'yen', 'cs'),
    },
    { key: 'cost_commute', label: '交通費', fmt: 'yen', values: costCommute, total: totalOf(costCommute), hint: `出勤者の交通費(往復)${inputNote(commuteIn)}`, children: byAppointer(isShinagawa, (r) => r.commute, 'yen', 'cc') },
    {
      key: 'cost_buai',
      label: '在宅歩合',
      fmt: 'yen',
      values: costBuai,
      total: totalOf(costBuai),
      hint: `取得件数 × 件数単価${inputNote(laborIn)}`,
      better: '売上に対して低いほど良い',
      children: byAppointer((r) => !r.is_manager && costKey(r) === 'buai', (r) => r.pay, 'yen', 'cb'),
    },
    {
      key: 'cost_jikyu',
      label: '在宅時給',
      fmt: 'yen',
      values: costJikyu,
      total: totalOf(costJikyu),
      hint: `時給 × BlueBeanの稼働時間(受付+通話+後処理)${inputNote(laborIn)}`,
      better: '売上に対して低いほど良い',
      children: byAppointer((r) => !r.is_manager && costKey(r) === 'jikyu', (r) => r.pay, 'yen', 'cj'),
    },
    { key: 'cost_timee', label: 'タイミー', fmt: 'yen', values: costTimee, total: totalOf(costTimee), hint: `タイミー(時給)の報酬${inputNote(laborIn)}`, children: byAppointer(isTimee, (r) => r.pay - r.commute, 'yen', 'ct') },
    {
      key: 'cost_timee_fee',
      label: 'タイミー振込手数料',
      fmt: 'yen',
      values: costTimeeFee,
      total: totalOf(costTimeeFee),
      hint: `その日に稼働したタイミーの人数 × ${formatYen(targets.timee_fee_per_person)}円(単価は「月次設定」)。稼働人数 合計 ${totalOf(timeeHeads)}人`,
      better: '低いほど良い',
    },
    { key: 'cost_sub', label: '小計', fmt: 'yen', values: costSub, total: totalOf(costSub), bold: true, summary: true },
    {
      key: 'cost_rate',
      label: '原価率',
      fmt: 'ratio',
      values: costRate,
      total: salesSubTotal > 0 ? (totalOf(costSub) / salesSubTotal) * 100 : null,
      colorRate: true,
      summary: true,
    },
    {
      key: 'gross',
      label: '粗利',
      fmt: 'yen',
      values: gross,
      total: grossTotal,
      bold: true,
      summary: true,
    },
    { key: 'gross_rate', label: '粗利率', fmt: 'ratio', values: grossRate, total: salesSubTotal > 0 ? (grossTotal / salesSubTotal) * 100 : null, hint: '粗利 ÷ 売上小計', better: '高いほど良い', bold: true, summary: true },
    { key: 'sec_exp', label: '経費 ／ 日別入力', fmt: 'yen', values: {}, total: null, section: true, hint: '「日報/サマリ」の経費画面で入れた金額(PLからは入力しない。D-57)。会社へは構成比で配賦' },
    ...expenseRows,
    { key: 'exp_sec_call', label: '経費 ／ 通話料', fmt: 'yen', values: {}, total: null, section: true },
    callFeeRow,
    { key: 'exp_total', label: '経費合計', fmt: 'yen', values: map(share, (s) => Math.round((expenseTotal * s) / 100)), total: expenseTotal, bold: true, summary: true },
    // ---- 営業利益 → 営業外経費(管理者報酬 = 営業利益 × 率) → 経常利益(現行「1日」シート 64〜86行) ----
    {
      key: 'op_profit',
      label: '営業利益',
      fmt: 'yen',
      values: map(gross, (g, id) => g - Math.round((expenseTotal * (share[id] ?? 0)) / 100)),
      total: grossTotal - expenseTotal,
      bold: true,
      summary: true,
    },
    { key: 'sec_nonsales', label: '営業外売上', fmt: 'yen', values: {}, total: null, section: true, hint: '現行「1日」シートの行。入力元が未確定のため 0(確認事項)' },
    { key: 'nonsales_total', label: '営業外売上 小計', fmt: 'yen', values: {}, total: 0, bold: true, summary: true, hint: '入力元が決まるまで 0' },
    { key: 'sec_nonop', label: '営業外経費', fmt: 'yen', values: {}, total: null, section: true, hint: '管理者報酬 = 営業利益 × 率(合計列のみ。率は「月次設定」)' },
    ...(targets.manager_bonus_rates ?? []).map((rate, i) => ({
      key: `mgr_bonus_${i}`,
      label: `管理者報酬(${rate}%)`,
      fmt: 'yen' as Fmt,
      values: {} as Record<string, number | null>,
      total: Math.round(((grossTotal - expenseTotal) * rate) / 100),
      hint: `営業利益 × ${rate}%(現行「1日」シートの営業外経費)`,
      better: '低いほど良い',
    })),
    {
      key: 'nonop_total',
      label: '営業外経費 小計',
      fmt: 'yen',
      values: {},
      total: (targets.manager_bonus_rates ?? []).reduce((s, rate) => s + Math.round(((grossTotal - expenseTotal) * rate) / 100), 0),
      bold: true,
      summary: true,
    },
    {
      key: 'ordinary',
      label: '経常利益',
      fmt: 'yen',
      values: {},
      total: grossTotal - expenseTotal - (targets.manager_bonus_rates ?? []).reduce((s, rate) => s + Math.round(((grossTotal - expenseTotal) * rate) / 100), 0),
      bold: true,
      summary: true,
    },
  ];

  const costSubtotal = totalOf(costSub);
  return {
    // 科目のまとまりごとに色を付ける(②階層版と同じ色。2026-09-11 指示)
    rows: plRows.map((r) => ({ ...r, tone: toneOf(r.key) })),
    appointments,
    visits: visitsTotal,
    expectedSales: totalOf(expected),
    salesSubtotal: salesSubTotal,
    costSubtotal,
    gross: salesSubTotal - costSubtotal,
    grossRate: salesSubTotal > 0 ? ((salesSubTotal - costSubtotal) / salesSubTotal) * 100 : null,
    costRate: salesSubTotal > 0 ? (costSubtotal / salesSubTotal) * 100 : null,
    expenseTotal,
  };
}
